import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../hooks/useSocket';
import { useTheme } from '../hooks/useTheme';
import api from '../api';

function useDeviceType() {
  const get = () => window.innerWidth >= 1200 ? 'desktop' : window.innerWidth >= 768 ? 'tablet' : 'mobile';
  const [type, setType] = useState(get);
  useEffect(() => {
    const h = () => setType(get());
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []); // eslint-disable-line
  return type;
}

const ICONS = {
  dashboard: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/></svg>,
  jobs:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>,
  matches:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>,
  messages:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
  team:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
  analytics: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  profile:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  bell:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>,
  logout:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  chevron:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>,
  billing:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
};

export default function Layout() {
  const { user, logout }   = useAuth();
  const navigate           = useNavigate();
  const location           = useLocation();
  const { on }             = useSocket();
  const deviceType         = useDeviceType();
  const isDesktop          = deviceType === 'desktop';
  const isTablet           = deviceType === 'tablet';
  const { theme, toggleTheme } = useTheme();
  const [notifs, setNotifs]       = useState([]);
  const [unread, setUnread]       = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const notifRef    = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    api.get('/api/notifications').then(r => {
      setNotifs(r.data.notifications || []);
      setUnread(r.data.unread_count  || 0);
    }).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    const offMsg   = on('new_message',      () => setUnread(u => u + 1));
    const offNotif = on('new_notification', (n) => {
      setUnread(u => u + 1);
      setNotifs(prev => [n, ...prev].slice(0, 20));
    });
    return () => { offMsg(); offNotif(); };
  }, [on]);

  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target) && showNotif) {
        setShowNotif(false);
        if (unread > 0) {
          api.patch('/api/notifications/read', { ids: 'all' })
            .then(() => { setUnread(0); setNotifs(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))); })
            .catch(() => {});
        }
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showNotif, unread]); // eslint-disable-line

  const openNotif = () => {
    const wasOpen = showNotif;
    setShowNotif(v => !v);
    if (wasOpen && unread > 0) {
      api.patch('/api/notifications/read', { ids: 'all' })
        .then(() => { setUnread(0); setNotifs(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))); })
        .catch(() => {});
    }
  };

  const candidateNav = [
    { path: '/explore',  label: 'Jobs',     icon: ICONS.jobs },
    { path: '/matches',  label: 'Matches',  icon: ICONS.matches },
    { path: '/messages', label: 'Messages', icon: ICONS.messages },
    { path: '/profile',  label: 'Profile',  icon: ICONS.profile },
  ];
  const recruiterNav = [
    { path: '/recruiter',  label: 'Dashboard', icon: ICONS.dashboard },
    { path: '/billing',    label: 'Credits',   icon: ICONS.billing },
    { path: '/messages',   label: 'Messages',  icon: ICONS.messages },
    { path: '/team-chat',  label: 'Team',      icon: ICONS.team },
    { path: '/stats',      label: 'Analytics', icon: ICONS.analytics },
    { path: '/profile',    label: 'Profile',   icon: ICONS.profile },
  ];
  const navItems = user?.role === 'recruiter' ? recruiterNav : candidateNav;
  const initial  = user?.full_name?.[0]?.toUpperCase() || '?';

  /* ── Notification dropdown ── */
  const NotifDropdown = ({ style }) => (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)',
      zIndex: 400, padding: 16, minWidth: 300, ...style,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '.9rem', color: 'var(--navy)' }}>Notifications</span>
        <button onClick={() => setShowNotif(false)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '.9rem', lineHeight: 1 }}>✕</button>
      </div>
      {notifs.filter(n => !n.read_at).length === 0 ? (
        <p style={{ fontSize: '.82rem', color: 'var(--muted)', padding: '12px 0', textAlign: 'center' }}>No new notifications.</p>
      ) : notifs.filter(n => !n.read_at).slice(0, 8).map(n => (
        <div key={n.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600, fontSize: '.82rem', color: 'var(--text)', marginBottom: 2 }}>{n.title}</div>
          {n.body && <div style={{ fontSize: '.75rem', color: 'var(--muted)' }}>{n.body}</div>}
          <div style={{ fontSize: '.68rem', color: 'var(--muted2)', marginTop: 3 }}>{new Date(n.created_at).toLocaleDateString()}</div>
        </div>
      ))}
    </div>
  );

  /* ═══════ DESKTOP ═══════ */
  if (isDesktop) {
    const SIDEBAR = 232;
    const TOPBAR  = 60;
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>

        {/* ── Sidebar ── */}
        <aside style={{
          width: SIDEBAR, flexShrink: 0,
          position: 'fixed', top: 0, left: 0, height: '100vh',
          background: 'var(--navy)',
          display: 'flex', flexDirection: 'column',
          zIndex: 200,
        }}>
          {/* Logo */}
          <div style={{ padding: '20px 18px 16px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
            <div onClick={() => navigate('/')} style={{ cursor:'pointer', lineHeight: 1 }}>
              <img src="/logo-white.svg" alt="SpotdHire" style={{ height: 28 }} />
              <div style={{ fontSize:'.66rem', color:'rgba(255,255,255,.35)', marginTop:4, letterSpacing:'.05em', textTransform:'uppercase', fontWeight:600 }}>
                {user?.role === 'recruiter' ? 'Recruiter' : 'Candidate'}
              </div>
            </div>
          </div>

          {/* Nav */}
          <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto' }}>
            {navItems.map(item => {
              const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <div
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', borderRadius: 9, marginBottom: 2,
                    cursor: 'pointer', transition: 'all .15s',
                    background: active ? 'rgba(255,255,255,.1)' : 'transparent',
                    color: active ? '#fff' : 'rgba(255,255,255,.5)',
                    fontWeight: active ? 700 : 500,
                    fontSize: '.88rem',
                    borderLeft: active ? '3px solid var(--purple)' : '3px solid transparent',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,.05)'; e.currentTarget.style.color = 'rgba(255,255,255,.8)'; }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,.5)'; } }}
                >
                  <div style={{ width: 18, height: 18, flexShrink: 0 }}>{item.icon}</div>
                  {item.label}
                  {item.label === 'Messages' && unread > 0 && (
                    <span style={{ marginLeft: 'auto', minWidth: 18, height: 18, padding: '0 5px', background: 'var(--red)', color: '#fff', borderRadius: 9, fontSize: '.77rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Bottom user area */}
          <div style={{ padding: '12px 10px', borderTop: '1px solid rgba(255,255,255,.07)' }}>
            <div
              onClick={() => { setShowUserMenu(false); logout(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 9, cursor: 'pointer', color: 'rgba(255,255,255,.4)', fontSize: '.82rem', fontWeight: 500, transition: 'all .15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,.05)'; e.currentTarget.style.color = 'rgba(255,255,255,.7)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,.4)'; }}
            >
              <div style={{ width: 18, height: 18 }}>{ICONS.logout}</div>
              Sign out
            </div>
          </div>
        </aside>

        {/* ── Top bar ── */}
        <header style={{
          position: 'fixed', top: 0, left: SIDEBAR, right: 0, height: TOPBAR,
          background: 'var(--surface)', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          padding: '0 24px', zIndex: 190, gap: 8,
        }}>
          {/* Theme toggle */}
          <button
            aria-label="Toggle theme"
            onClick={toggleTheme}
            style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '.9rem', transition: 'all .15s' }}
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>

          {/* Notifications */}
          <div ref={notifRef} style={{ position: 'relative' }}>
            <button
              onClick={openNotif}
              style={{ width: 36, height: 36, borderRadius: 8, background: showNotif ? 'var(--purple-dim)' : 'var(--bg)', border: `1px solid ${showNotif ? 'rgba(99,91,255,.25)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', transition: 'all .15s', color: showNotif ? 'var(--accent)' : 'var(--muted)' }}
            >
              <div style={{ width: 18, height: 18 }}>{ICONS.bell}</div>
              {unread > 0 && (
                <span style={{ position: 'absolute', top: -3, right: -3, minWidth: 16, height: 16, padding: '0 4px', background: 'var(--red)', color: '#fff', borderRadius: 8, border: '2px solid var(--surface)', fontSize: '.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>
            {showNotif && <NotifDropdown style={{ position: 'absolute', top: 44, right: 0 }} />}
          </div>

          {/* User */}
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowUserMenu(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px 5px 5px', borderRadius: 9, background: showUserMenu ? 'var(--bg)' : 'transparent', border: `1px solid ${showUserMenu ? 'var(--border)' : 'transparent'}`, cursor: 'pointer', transition: 'all .15s' }}
            >
              <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '.78rem', fontWeight: 800, flexShrink: 0 }}>{initial}</div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '.84rem', fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.full_name}</div>
                <div style={{ fontSize: '.7rem', color: 'var(--muted)', whiteSpace: 'nowrap', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email}</div>
              </div>
              <div style={{ width: 14, height: 14, color: 'var(--muted2)' }}>{ICONS.chevron}</div>
            </button>
            {showUserMenu && (
              <div style={{ position: 'absolute', top: 46, right: 0, width: 188, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)', zIndex: 400, overflow: 'hidden' }}>
                <div
                  onClick={() => { setShowUserMenu(false); navigate('/profile'); }}
                  style={{ padding: '11px 14px', fontSize: '.83rem', color: 'var(--text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9, borderBottom: '1px solid var(--border)', transition: 'background .12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ width: 16, height: 16 }}>{ICONS.profile}</div>
                  My profile
                </div>
                <div
                  onClick={() => { setShowUserMenu(false); logout(); }}
                  style={{ padding: '11px 14px', fontSize: '.83rem', color: 'var(--red)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9, transition: 'background .12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ width: 16, height: 16 }}>{ICONS.logout}</div>
                  Sign out
                </div>
              </div>
            )}
          </div>
        </header>

        {/* ── Main content ── */}
        <main style={{ marginLeft: SIDEBAR, flex: 1, minHeight: '100vh', paddingTop: TOPBAR, display: 'flex', flexDirection: 'column' }}>
          <Outlet />
        </main>
      </div>
    );
  }

  /* ═══════ TABLET (768–1199px) ═══════ */
  if (isTablet) {
    const TOPBAR = 60;
    return (
      <>
        <header style={{
          position:'fixed', top:0, left:0, right:0, height:TOPBAR,
          background:'var(--surface)', borderBottom:'1px solid var(--border)',
          display:'flex', alignItems:'center', padding:'0 16px 0 20px',
          zIndex:200, gap:0, boxShadow:'0 1px 4px rgba(10,37,64,.06)',
        }}>
          {/* Logo */}
          <div onClick={()=>navigate('/')} style={{cursor:'pointer',flexShrink:0,marginRight:20}}>
            <img src="/logo.svg" alt="SpotdHire" style={{height:28,display:'block'}}/>
          </div>

          {/* Nav inline */}
          <nav style={{display:'flex',flex:1,gap:2,overflowX:'auto',scrollbarWidth:'none',WebkitOverflowScrolling:'touch'}}>
            {navItems.map(item=>{
              const active = location.pathname===item.path || location.pathname.startsWith(item.path+'/');
              return (
                <button key={item.path} onClick={()=>navigate(item.path)} style={{
                  display:'flex',alignItems:'center',gap:6,padding:'7px 11px',borderRadius:8,
                  border:'none',background:active?'var(--purple-dim)':'transparent',
                  color:active?'var(--accent)':'var(--muted)',fontWeight:active?700:500,
                  fontSize:'.82rem',cursor:'pointer',whiteSpace:'nowrap',transition:'all .15s',
                  fontFamily:'var(--font)',flexShrink:0,position:'relative',
                }}>
                  <div style={{width:16,height:16,flexShrink:0}}>{item.icon}</div>
                  {item.label}
                  {item.label==='Messages'&&unread>0&&(
                    <span style={{position:'absolute',top:4,right:4,width:7,height:7,background:'var(--red)',borderRadius:'50%'}}/>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Right actions */}
          <div style={{display:'flex',alignItems:'center',gap:6,marginLeft:8,flexShrink:0}}>
            <button aria-label="Toggle theme" onClick={toggleTheme} style={{width:34,height:34,borderRadius:8,background:'var(--bg)',border:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',fontSize:'.85rem'}}>
              {theme==='dark'?'☀️':'🌙'}
            </button>
            <div ref={notifRef} style={{position:'relative'}}>
              <button onClick={openNotif} style={{width:34,height:34,borderRadius:8,background:unread>0?'var(--red-dim)':'var(--bg)',border:`1px solid ${unread>0?'rgba(220,38,38,.2)':'var(--border)'}`,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',position:'relative',color:unread>0?'var(--red)':'var(--muted)'}}>
                <div style={{width:17,height:17}}>{ICONS.bell}</div>
                {unread>0&&<span style={{position:'absolute',top:-2,right:-2,minWidth:15,height:15,padding:'0 3px',background:'var(--red)',color:'#fff',borderRadius:8,border:'2px solid var(--surface)',fontSize:'.74rem',fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center'}}>{unread>9?'9+':unread}</span>}
              </button>
              {showNotif&&<NotifDropdown style={{position:'absolute',top:42,right:0}}/>}
            </div>
            <div ref={userMenuRef} style={{position:'relative'}}>
              <button onClick={()=>setShowUserMenu(v=>!v)} style={{display:'flex',alignItems:'center',gap:7,padding:'4px 10px 4px 4px',borderRadius:9,background:showUserMenu?'var(--bg)':'transparent',border:`1px solid ${showUserMenu?'var(--border)':'transparent'}`,cursor:'pointer',transition:'all .15s'}}>
                <div style={{width:28,height:28,borderRadius:7,background:'var(--navy)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:'.75rem',fontWeight:800,flexShrink:0}}>{initial}</div>
                <div style={{width:12,height:12,color:'var(--muted2)'}}>{ICONS.chevron}</div>
              </button>
              {showUserMenu&&createPortal(
                <div onMouseDown={e=>e.stopPropagation()} style={{position:'fixed',top:68,right:16,width:192,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius)',boxShadow:'var(--shadow-lg)',zIndex:9999,overflow:'hidden'}}>
                  <div style={{padding:'11px 14px',borderBottom:'1px solid var(--border)'}}>
                    <div style={{fontSize:'.83rem',fontWeight:700,color:'var(--navy)'}}>{user?.full_name}</div>
                    <div style={{fontSize:'.7rem',color:'var(--muted)',marginTop:2}}>{user?.email}</div>
                  </div>
                  <div onClick={()=>{setShowUserMenu(false);navigate('/profile');}} style={{padding:'11px 14px',fontSize:'.83rem',color:'var(--text)',cursor:'pointer',display:'flex',alignItems:'center',gap:9,borderBottom:'1px solid var(--border)'}} onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <div style={{width:15,height:15}}>{ICONS.profile}</div>My profile
                  </div>
                  <div onClick={()=>{setShowUserMenu(false);logout();}} style={{padding:'11px 14px',fontSize:'.83rem',color:'var(--red)',cursor:'pointer',display:'flex',alignItems:'center',gap:9}} onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <div style={{width:15,height:15}}>{ICONS.logout}</div>Sign out
                  </div>
                </div>,
                document.body
              )}
            </div>
          </div>
        </header>

        <main style={{paddingTop:TOPBAR,minHeight:`calc(100vh - ${TOPBAR}px)`}}>
          <Outlet/>
        </main>
      </>
    );
  }

  /* ═══════ MOBILE (<768px) ═══════ */
  return (
    <>
      {/* Top bar */}
      <div style={{
        position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 430,
        height: 'calc(56px + env(safe-area-inset-top))',
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        padding: '0 16px', paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 10, boxSizing: 'border-box', zIndex: 200,
      }}>
        <div
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer', lineHeight: 1 }}
        >
          <img src="/logo.svg" alt="SpotdHire" style={{ height: 30 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button aria-label="Toggle theme" onClick={toggleTheme} style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '.85rem' }}>
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
          <div ref={notifRef} style={{ position: 'relative' }}>
            <button onClick={openNotif} style={{ width: 34, height: 34, borderRadius: 8, background: unread > 0 ? 'var(--red-dim)' : 'var(--bg)', border: `1px solid ${unread > 0 ? 'rgba(220,38,38,.2)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', color: unread > 0 ? 'var(--red)' : 'var(--muted)' }}>
              <div style={{ width: 17, height: 17 }}>{ICONS.bell}</div>
              {unread > 0 && <span style={{ position: 'absolute', top: -2, right: -2, minWidth: 15, height: 15, padding: '0 3px', background: 'var(--red)', color: '#fff', borderRadius: 8, border: '2px solid var(--surface)', fontSize: '.74rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unread > 9 ? '9+' : unread}</span>}
            </button>
            {showNotif && <NotifDropdown style={{ position: 'absolute', top: 42, right: 0 }} />}
          </div>
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <div
              onClick={() => setShowUserMenu(v => !v)}
              style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '.78rem', fontWeight: 800, cursor: 'pointer' }}
            >
              {initial}
            </div>
            {showUserMenu && createPortal(
              <div onMouseDown={e => e.stopPropagation()} style={{ position: 'fixed', top: 'calc(62px + env(safe-area-inset-top))', right: 16, width: 200, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)', zIndex: 9999, overflow: 'hidden' }}>
                <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '.83rem', fontWeight: 700, color: 'var(--navy)' }}>{user?.full_name}</div>
                  <div style={{ fontSize: '.7rem', color: 'var(--muted)', marginTop: 2 }}>{user?.email}</div>
                </div>
                <div onClick={() => { setShowUserMenu(false); navigate('/profile'); }} style={{ padding: '12px 14px', fontSize: '.83rem', color: 'var(--text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9, borderBottom: '1px solid var(--border)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: 15, height: 15 }}>{ICONS.profile}</div> My profile
                </div>
                <div onClick={() => { setShowUserMenu(false); logout(); }} style={{ padding: '12px 14px', fontSize: '.83rem', color: 'var(--red)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9 }} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: 15, height: 15 }}>{ICONS.logout}</div> Sign out
                </div>
              </div>,
              document.body
            )}
          </div>
        </div>
      </div>

      {/* Main content — safe-area-top handled by html, only clear the fixed top bar */}
      <div className="page-scroll" style={{ paddingTop: '56px' }}>
        <Outlet />
      </div>

      {/* Bottom nav */}
      <nav style={{
        position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 430,
        height: 'calc(60px + var(--safe-bottom))',
        background: 'var(--surface)', borderTop: '1px solid var(--border)',
        display: 'flex', alignItems: 'flex-start', paddingTop: 10,
        zIndex: 200,
      }}>
        {navItems.map(item => {
          const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, border: 'none', background: 'none', cursor: 'pointer', padding: '2px 0', WebkitTapHighlightColor: 'transparent', color: active ? 'var(--accent)' : 'var(--muted2)', transition: 'color .15s' }}
            >
              <div style={{ width: 21, height: 21 }}>{item.icon}</div>
              <span style={{ fontSize: '.74rem', fontWeight: active ? 700 : 500, letterSpacing: '.03em', textTransform: 'uppercase' }}>{item.label}</span>
            </button>
          );
        })}
      </nav>
      <div style={{ height: 'calc(72px + var(--safe-bottom))' }} />
    </>
  );
}
